import { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { heroAssets } from '@/mocks/hero';
import AtticScene from './AtticScene';
import EntranceBlinds from './EntranceBlinds';
import HeroNav from './HeroNav';
import HeroContent, { ScrollCue } from './HeroContent';
import HeroFloatingType from './HeroFloatingType';

gsap.registerPlugin(ScrollTrigger, useGSAP);

type Cfg = { end: string; scale: number; tilt: number; k: number };

export default function WindowPortalHero() {
  const root = useRef<HTMLElement>(null);

  useGSAP(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // ---- Entrance (unchanged) ----
    if (!reduce) {
      gsap.timeline({ delay: 0.22 })
        .to('.wp-strip', { scaleY: 0, duration: 0.62, ease: 'power3.inOut', stagger: 0.05 })
        .to('.wp-window-group', { opacity: 1, filter: 'blur(0px)', duration: 0.9, ease: 'power2.out' }, 0.15)
        .to(document.querySelectorAll('.wp-ui-fade'), { opacity: 1, duration: 0.5, ease: 'power2.out' }, 0.95)
        .fromTo('.wp-float', { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out', stagger: 0.12 }, 0.7)
        .set('.wp-entrance', { display: 'none' });
    } else {
      gsap.set('.wp-float', { opacity: 1 });
    }

    const mm = gsap.matchMedia();
    const build = (c: Cfg) => {
      const forest = root.current?.querySelector<HTMLElement>('.wp-forest');
      const travel = () => -((forest?.offsetHeight ?? window.innerHeight * 2) - window.innerHeight);

      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: root.current,
          start: 'top top',
          end: c.end,
          pin: true,
          scrub: 0.8,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      // Stage 1 (0 – 0.35): foreground fly-through; forest stays scale 1, drifts slightly down
      tl.to('.wp-cue', { opacity: 0, duration: 0.06 }, 0)
        .set('.wp-stage', { transformPerspective: 900 }, 0)
        .to('.wp-stage', { scale: c.scale, duration: 0.35, ease: 'power2.in' }, 0)
        .to('.wp-stage', { rotateX: c.tilt, duration: 0.25, ease: 'power1.in' }, 0.1)
        .set('.wp-stage', { visibility: 'hidden' }, 0.35)
        .to('.wp-forest', { y: () => window.innerHeight * -0.06, duration: 0.35 }, 0)
        // Floating type: perspective builds on approach (0 – 0.18), relaxes while exiting (0.18 – 0.3)
        .to('.wp-fl-group', { x: -80 * c.k, y: -26 * c.k, rotateY: -11, rotateX: 4, skewY: -3.4, duration: 0.18, ease: 'power1.in' }, 0)
        .to('.wp-fl-group', { x: -135 * c.k - 260, y: -45 * c.k, rotateY: 0, rotateX: 0, skewY: 0, opacity: 0, duration: 0.12, ease: 'power2.in' }, 0.18)
        .to('.wp-fr-group', { x: 92 * c.k, y: 36 * c.k, rotateY: 13, rotateX: -4, skewY: 3.1, duration: 0.18, ease: 'power1.in' }, 0)
        .to('.wp-fr-group', { x: 158 * c.k + 260, y: 62 * c.k, rotateY: 0, rotateX: 0, skewY: 0, opacity: 0, duration: 0.12, ease: 'power2.in' }, 0.18)
        .to('.wp-fl-line', { x: (i: number) => -(i * 18 + 6) * c.k, y: (i: number) => -(i * 6) * c.k, duration: 0.3, ease: 'power1.in' }, 0)
        .to('.wp-fr-line', { x: (i: number) => (i * 20 + 6) * c.k, y: (i: number) => (i * 7) * c.k, duration: 0.3, ease: 'power1.in' }, 0)
        // Stage 2 (0.35 – 0.75): same forest, translateY only
        .to('.wp-forest', { y: travel, duration: 0.4, ease: 'power1.inOut' }, 0.35)
        // Stage 3 (0.75 – 1): solid colour takes over, then floating type
        .fromTo('.wp-solid', { opacity: 0 }, { opacity: 1, duration: 0.12 }, 0.75)
        .set('.wp-content', { visibility: 'visible', opacity: 1 }, 0.86)
        .fromTo(
          '.wp-reveal',
          { opacity: 0, y: 70, rotateX: -55, z: -120, transformPerspective: 800, transformOrigin: '50% 100%' },
          { opacity: 1, y: 0, rotateX: 0, z: 0, duration: 0.11, stagger: 0.012, ease: 'power3.out' },
          0.86,
        );
    };

    mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => build({ end: '+=320%', scale: 9, tilt: 10, k: 1 }));
    mm.add('(min-width: 768px) and (max-width: 1023px) and (prefers-reduced-motion: no-preference)', () => build({ end: '+=280%', scale: 8, tilt: 8, k: 0.7 }));
    mm.add('(max-width: 767px) and (prefers-reduced-motion: no-preference)', () => build({ end: '+=230%', scale: 7, tilt: 5, k: 0.45 }));

    // eslint-disable-next-line consistent-return
    return () => mm.revert();
  }, { scope: root });

  return (
    <>
      <link rel="preload" as="image" href={heroAssets.atticForeground} />
      <link rel="preload" as="image" href={heroAssets.forest} />
      <section ref={root} className="wp-hero relative h-[100svh] w-full overflow-hidden bg-foreground-950" aria-label="Hero">
        {/* Single continuous forest background, present from frame one */}
        <div className="wp-forest absolute inset-x-0 top-0 h-[200%] will-change-transform">
          <img
            src={heroAssets.forest}
            alt="Sunlit green forest seen from beneath the canopy"
            title="Forest view through a roof window"
            className="h-full w-full object-cover object-top"
            fetchPriority="high"
            decoding="async"
          />
        </div>
        <AtticScene />
        {/* Solid colour section */}
        <div className="wp-solid pointer-events-none absolute inset-0 z-[15] bg-primary-800 opacity-0" />
        <HeroFloatingType />
        <HeroContent />
        <HeroNav />
        <ScrollCue />
        <EntranceBlinds />
      </section>
    </>
  );
}